import { goto } from '$app/navigation';
import { browser } from '$app/environment';
import { sessionUnconfirmed } from '$lib/stores/sessionState';
import { decideUnauthorized } from './authRedirectGuard';
import { returnToQuery } from '$lib/features/auth/logic/returnTo';
// Używamy względnych URLi - proxy w hooks.server.ts obsługuje przekierowania

const STATUS_CODES: Record<number, string> = {
	400: 'INVALID_INPUT',
	401: 'AUTH_REQUIRED',
	403: 'FORBIDDEN',
	404: 'NOT_FOUND',
	409: 'CONFLICT',
	413: 'PAYLOAD_TOO_LARGE',
	422: 'INVALID_INPUT',
	429: 'RATE_LIMITED',
	500: 'INTERNAL_ERROR',
	502: 'UPSTREAM_BAD_GATEWAY',
	503: 'SERVICE_UNAVAILABLE',
	504: 'UPSTREAM_TIMEOUT'
};
const PUBLIC_ERROR_CODES = new Set([
	...Object.values(STATUS_CODES),
	'AI_INTERNAL_ONLY',
	'AI_QUICK_ADD_CONSENT_REQUIRED',
	'AI_QUICK_ADD_CONSENT_VERSION_MISMATCH',
	'AI_QUICK_ADD_DAILY_LIMIT_REACHED',
	'API_KEY_INVALID',
	'API_KEY_LIMIT_EXCEEDED',
	'API_KEY_MISSING',
	'API_KEY_NOT_FOUND',
	'API_KEY_READ_ONLY',
	'AUTH_EMAIL_CONFLICT',
	'AUTH_EMAIL_UNVERIFIABLE',
	'AUTH_EMAIL_VERIFICATION_UNAVAILABLE',
	'AUTH_INVALID_TOKEN',
	'AUTH_REQUIRED',
	'AUTH_SERVICE_UNAVAILABLE',
	'AUTH_TOKEN_EXPIRED',
	'AUTH_TOKEN_MISSING_USER_ID',
	'AUTH_TOKEN_VERIFICATION_FAILED',
	'AUTH_USER_INACTIVE',
	'AUTO_DELETION_JOB_FAILED',
	'AUTO_DELETION_PREVIEW_FAILED',
	'BILLING_INTERVAL_UNAVAILABLE',
	'CATEGORIES_NOT_FOUND',
	'CATEGORY_HIERARCHY_ERROR',
	'CATEGORY_INVALID_DATA',
	'CATEGORY_NAME_DUPLICATE',
	'CATEGORY_NOT_FOUND',
	'CATEGORY_PARENT_NOT_FOUND',
	'CATEGORY_VALIDATION_ERROR',
	'CONFLICT',
	'CONVERSION_NOT_FOUND',
	'CONVERSION_VALIDATION_ERROR',
	'ENTRY_INVALID_DATA',
	'ENTRY_NOT_FOUND',
	'ENTRY_REQUEST_FAILED',
	'FAVORITE_INVALID_DATA',
	'FAVORITE_ITEM_NOT_FOUND',
	'FORBIDDEN',
	'HEALTH_CONSENT_AI_NOT_COVERED',
	'HEALTH_CONSENT_REQUIRED',
	'HEALTH_CONSENT_VERSION_MISMATCH',
	'INVALID_INPUT',
	'INVALID_PERIOD',
	'INVALID_STATUS',
	'INVALID_TREND_METRIC_SELECTION',
	'INVALID_WINDOW_DAYS',
	'INTEGRATION_SOURCE_MISMATCH',
	'JOB_STATS_FAILED',
	'METRICS_MISMATCH',
	'METRIC_NOT_FOUND',
	'METRIC_SCHEMA_CONFLICT_HAS_HISTORY',
	'METRIC_VALIDATION_ERROR',
	'NOT_FOUND',
	'PUBLIC_STRAVA_DISABLED',
	'QUICK_ADD_LLM_PENDING_LEGAL',
	'QUICK_ADD_TRACKER_REQUIRED',
	'RATE_LIMIT_EXCEEDED',
	'REQUEST_FAILED',
	'SERVER_ERROR',
	'SERVICE_UNAVAILABLE',
	'SIGNUP_CLOSED',
	'SUGGESTIONS_FETCH_ERROR',
	'TAG_ALREADY_EXISTS',
	'TASK_NOT_FOUND',
	'TIMELINE_FETCH_ERROR',
	'TOO_MANY_REQUESTS',
	'TRACKER_BUNDLE_INVALID',
	'TRACKER_INVALID_CONFIG',
	'TRACKER_INVALID_STATE',
	'TRACKER_LIMIT_REACHED',
	'TRACKER_NOT_FOUND',
	'TRACKER_NOTIFICATION_CONFIG_NOT_FOUND',
	'TRACKER_SERVICE_ERROR',
	'TRACKER_STATS_UNREPRESENTABLE',
	'UPSTREAM_TIMEOUT',
	'VIEW_FETCH_ERROR',
	'ai_chat_free_limit',
	'ai_chat_monthly_limit',
	'category_limit_reached',
	'insufficient_data',
	'llm_generation_failed',
	'metric_limit_reached',
	'no_report',
	'pro_required',
	'template_not_found',
	'template_not_instantiable',
	'tracker_limit_reached'
]);

function isStableErrorCode(value: unknown): value is string {
	return typeof value === 'string' && PUBLIC_ERROR_CODES.has(value);
}

export interface ApiFailure {
	code: string;
	status: number;
	retryAfter?: number;
}

export function sanitizeApiFailure(error: unknown, fallbackCode: string): ApiFailure | Error {
	if (typeof error === 'object' && error !== null) {
		const candidate = error as { code?: unknown; status?: unknown; retryAfter?: unknown };
		if (
			isStableErrorCode(candidate.code) &&
			typeof candidate.status === 'number' &&
			Number.isInteger(candidate.status)
		) {
			const safe: ApiFailure = { code: candidate.code, status: candidate.status };
			if (typeof candidate.retryAfter === 'number' && Number.isFinite(candidate.retryAfter)) {
				safe.retryAfter = candidate.retryAfter;
			}
			return safe;
		}
	}
	if (error instanceof TypeError) return new Error('NETWORK_REQUEST_UNCONFIRMED');
	return new Error(isStableErrorCode(fallbackCode) ? fallbackCode : 'REQUEST_FAILED');
}

function stableErrorCode(status: number, body: unknown): string {
	const record =
		typeof body === 'object' && body !== null ? (body as Record<string, unknown>) : null;
	const detail = record?.detail;
	const candidate =
		typeof record?.error_code === 'string'
			? record.error_code
			: typeof detail === 'string'
				? detail
				: typeof detail === 'object' && detail !== null && 'error_code' in detail
					? (detail as { error_code?: unknown }).error_code
					: undefined;
	return isStableErrorCode(candidate) ? candidate : (STATUS_CODES[status] ?? 'REQUEST_FAILED');
}

// One login redirect at a time: a burst of parallel 401s triggers one navigation.
let loginRedirectInFlight = false;

async function handleUnauthorized(): Promise<void> {
	if (loginRedirectInFlight) return;
	const action = decideUnauthorized({
		pathname: typeof window === 'undefined' ? '' : window.location.pathname,
		now: Date.now(),
		storage: typeof sessionStorage === 'undefined' ? null : sessionStorage
	});
	if (action === 'ignore') return;
	if (action === 'session-unconfirmed') {
		// samlla#0xhq: the 401 repeats inside the redirect window (or right after
		// returning from login) — show the neutral state, never log out.
		sessionUnconfirmed.set(true);
		return;
	}
	// samlla#0xhq: no clearAuth() here — clearing auth state and drafts belongs
	// to the explicit logout action only; the login page re-establishes auth.
	loginRedirectInFlight = true;
	// samlla#yy28: come back to this page (path + query, `/pl/` kept) after login.
	const query =
		typeof window === 'undefined'
			? ''
			: returnToQuery(`${window.location.pathname}${window.location.search ?? ''}`);
	const { resolve } = await import('$app/paths');
	// Dynamic import keeps non-SvelteKit logic-test workers from resolving the virtual module.
	// eslint-disable-next-line svelte/no-navigation-without-resolve
	void Promise.resolve(goto(`${resolve('/auth/login')}${query}`)).finally(() => {
		loginRedirectInFlight = false;
	});
}

/**
 * SML-140: every generated call — TanStack queries and mutations as well as
 * direct calls from stores and API wrappers that rewrap the error — reports a
 * 403 HEALTH_CONSENT_REQUIRED here, before the caller sees it. The query
 * client registers the tab's reaction (drop tenant content, refresh /me).
 */
type HealthConsentRequiredListener = (failure: { code: string; status: number }) => void;

let healthConsentRequiredListener: HealthConsentRequiredListener | null = null;

export function setHealthConsentRequiredListener(
	listener: HealthConsentRequiredListener | null
): void {
	healthConsentRequiredListener = listener;
}

function notifyHealthConsentRequired(failure: { code: string; status: number }): void {
	if (failure.code !== 'HEALTH_CONSENT_REQUIRED') return;
	try {
		healthConsentRequiredListener?.(failure);
	} catch {
		// The reaction is best effort; the caller still receives the API error.
	}
}

export const customInstance = async <T>(
	config:
		| {
				url: string;
				method?: string;
				headers?: HeadersInit;
				data?: unknown;
				params?: object;
				signal?: AbortSignal;
		  }
		| string,
	options?: RequestInit
): Promise<T> => {
	// Handle both old string format and new config object format
	let url: string;
	let finalOptions: RequestInit;

	if (typeof config === 'string') {
		// Old format - backward compatibility
		url = config;
		finalOptions = options || {};
	} else {
		// New format from Orval svelte-query
		url = config.url;

		// Handle different content types
		let body: BodyInit | undefined;
		if (config.data instanceof URLSearchParams) {
			// Form URL encoded data - send as is
			body = config.data;
		} else if (config.data instanceof FormData) {
			// Form data - send as is
			body = config.data;
		} else if (config.data !== undefined) {
			// JSON data - stringify it
			body = JSON.stringify(config.data);
		}

		finalOptions = {
			method: config.method || 'GET',
			headers: config.headers,
			body,
			signal: config.signal,
			...options
		};

		// Add query params to URL if present
		if (config.params) {
			const params = new URLSearchParams();
			Object.entries(config.params).forEach(([key, value]) => {
				if (value !== undefined && value !== null) {
					params.append(key, String(value));
				}
			});
			const queryString = params.toString();
			if (queryString) {
				url += (url.includes('?') ? '&' : '?') + queryString;
			}
		}
	}

	// Get token from Kinde session via server endpoint
	let token: string | null = null;
	// samlla#0xhq: a token endpoint that is unreachable or failing (not a 401)
	// is transient — the resulting API 401 must not trigger a login redirect.
	let tokenUnavailable = false;
	if (browser) {
		try {
			const tokenResponse = await fetch('/api/auth/token', {
				credentials: 'include'
			});
			if (tokenResponse.ok) {
				const tokenData = await tokenResponse.json();
				token = tokenData.token;
			} else if (tokenResponse.status !== 401) {
				tokenUnavailable = true;
			}
		} catch {
			// Missing token is handled by the server; never log auth errors or token context.
			tokenUnavailable = true;
		}
	}

	// Build headers
	const headers = new Headers(finalOptions.headers);

	// Set default content-type if not provided
	if (!headers.has('Content-Type') && finalOptions.body) {
		if (finalOptions.body instanceof URLSearchParams) {
			// URLSearchParams already sets the correct content-type
		} else if (finalOptions.body instanceof FormData) {
			// FormData sets its own content-type with boundary
		} else if (typeof finalOptions.body === 'string') {
			headers.set('Content-Type', 'application/json');
		}
	}

	// Add auth token if available
	if (token) {
		headers.set('Authorization', `Bearer ${token}`);
	}

	const response = await fetch(url, {
		...finalOptions,
		headers,
		credentials: 'include' // Include Kinde cookies
	});

	// Handle 401 Unauthorized specifically
	if (response.status === 401 && browser && !tokenUnavailable) {
		await handleUnauthorized();
	}

	// Handle different response types based on status
	if (!response.ok) {
		const contentType = response.headers.get('content-type') ?? '';
		let parsedBody: unknown = null;
		try {
			if (contentType.includes('application/json')) {
				parsedBody = await response.json();
			}
		} catch {
			parsedBody = null;
		}

		const retryAfterHeader = response.headers.get('retry-after');
		const retryAfter = retryAfterHeader ? Number(retryAfterHeader) : NaN;
		const errorObj: { code: string; status: number; retryAfter?: number } = {
			code: stableErrorCode(response.status, parsedBody),
			status: response.status
		};
		if (Number.isFinite(retryAfter) && retryAfter >= 0) errorObj.retryAfter = retryAfter;

		notifyHealthConsentRequired(errorObj);
		throw errorObj;
	}

	// Return data directly for svelte-query
	const data =
		response.headers.get('content-length') === '0' || response.status === 204
			? undefined
			: await response.json();

	return data as T;
};
